import Card from './Card';
import { DESIGNER, BLEED_AREA } from '../constants';
import ImageItem from './ImageItem';
import OverlayImageItem from './OverlayItem'
import TextItem from './TextItem';
import MonogramItem from './MonogramItem'
import DoodleItem from './DoodleItem';
import { createQuery } from '../../core/utils';
import AppSettings from '../AppSettings';
import { getEventPosition, roundRect } from './canvas-helper';
import Hammer from 'hammerjs';
import * as api from '../net/api';
import { CUSTOM_IMAGE_TYPE_ID } from '../constants';
import { LAYER_TYPE } from '../constants';
import { IMAGE, DOODLE, TEXT, CARD, CUSTOM_IMAGE, LOGO, SIGNATURE, STOCK_IMAGE, CLIPART, OVERLAY, MONOGRAM } from './itemTypes';


// getTouchPos() returns position of touch event relative to ssg-canvas element ---------------------
// see canvas-helper.js getEventPosition() for mouse version.
const getTouchPos = (e) => {
	const rect = e.target.getBoundingClientRect();
	const eventPos = e.center;

	return {
		x: eventPos.x - rect.left - rect.width / 2,
		y: eventPos.y - rect.top - rect.height / 2
	};
};

class Canvas {

	constructor(canvasElement, options = {}) {

		AppSettings.portraitMode = options.isPortraitSkin;
		
		const onLoaded = options.onLoaded || (() => { });
		this.onCanvasItemSelect = options.onCanvasItemSelect || (() => { });
		this.onCanvaDeselect = options.onCanvasItemDeselect || (() => { });
		this.onCanvasItemAllOut = options.onCanvasItemCoverageOut || (() => { });
		this.uploadOnlyMode = options.uploadOnly;

		this.canvas = canvasElement ? document.querySelector(canvasElement) : document.createElement('canvas');
		if (!this.canvas.id) {
			this.canvas.id = 'ssg-temp-canvas';
		}

		this.ctx = this.canvas.getContext('2d');

		let { width, height } = options;
		if (!width || !height) {
			const rect = this.canvas.getBoundingClientRect();
			width = rect.width;
			height = rect.height;
		}

		this.canvas.setAttribute('width', `${width}`);
		this.canvas.setAttribute('height', `${height}`);
		this.canvas.width = width;
		this.canvas.height = height;

		// Metal Skin Flag
		this.isMetalSkin = ( options.skinName === "Metal") ;
		
		//for debugging---------
		this.appsettings = AppSettings;
		this.customTypeId = CUSTOM_IMAGE_TYPE_ID;

		this.orientation = window.orientation; //portrait = 0 or 180, landscape = 90 or -90

		this.croppedSignatureImage = '';

		if (options.margin) {
			this.margin = options.margin;
		}

		if (options.isResponsible) {
			
			window.addEventListener('resize', () => {

				if(this.orientation !== window.orientation){
					this.orientation = window.orientation;

					setTimeout(this.handleResize.bind(this),100);  //allow delay for DOM/CSS to update
				}else{
					this.handleResize();
				} 
			});
		}

		this.defaultCoverage = { Left: 0, Right: 242, Top: 0, Bottom: 151 }

		this.handleCardCoverage = options.onCardCoverage || function () { };

		//allow for portrait or landscape image mode (i.e. 'vertical' products)
		const lsWidth = (AppSettings.designerSettings.Desktop.Right - AppSettings.designerSettings.Desktop.Left) || 241;
		const lsHeight = (AppSettings.designerSettings.Desktop.Bottom - AppSettings.designerSettings.Desktop.Top) || 153;
		
		this.itemArea = AppSettings.designerSettings.Coverage.Signature || AppSettings.designerSettings.Coverage.Image;

		//set template dimensions according to orientation
		this.template = {
			width: (AppSettings.portraitMode)? lsHeight : lsWidth,
			height: (AppSettings.portraitMode)? lsWidth : lsHeight,
			initialSize: {
				width: (AppSettings.portraitMode)? lsHeight : lsWidth,
				height: (AppSettings.portraitMode)? lsWidth : lsHeight
			}
		}

		if(AppSettings.portraitMode) options.scale = 0.2;  //cf's arbitary test value

		// scale template size
		if (options.scale) {
			//this.scale = options.scale;
			const ratio = this.template.width / this.template.height;
			this.template.width *= options.scale;
			this.template.height = this.template.width / ratio;
		} else if (this.margin) { // or put margins

			const ratio = this.template.width / this.template.height;
			this.template.width = this.canvas.width - (2 * this.margin);
			this.template.height = Math.floor(this.template.width / ratio);
		}
		else { //If neither scale or margins are provided (used for metal now)
			this.template.width = this.canvas.width;
			this.template.height = this.canvas.height;
		}

		this.items = [];
		this.activeMode = CARD;
		this.exclusiveMode = true; //sets canvas to allow only one feature (as set by activeMode above) to be active.

		const canvasCenter = {
			x: this.canvas.width / 2,
			y: this.canvas.height / 2
		};

		const cardCoverage = AppSettings.designerSettings.Coverage.Image;
		const canvasScale = this.getScale();

		let coverageArea = {};
		
		if(AppSettings.portraitMode) {
			coverageArea.x =  (this.template.width / 2) - (cardCoverage.Bottom * canvasScale),
			coverageArea.y =  (-this.template.height / 2 + cardCoverage.Left * canvasScale),
			coverageArea.height =  ((cardCoverage.Right - cardCoverage.Left) * canvasScale),
			coverageArea.width =  ((cardCoverage.Bottom - cardCoverage.Top) * canvasScale)
		} else {
			coverageArea.x =   (-this.template.width / 2 + cardCoverage.Left * canvasScale),
			coverageArea.y =   (-this.template.height / 2 + cardCoverage.Top * canvasScale),
			coverageArea.width =  ((cardCoverage.Right - cardCoverage.Left) * canvasScale),
			coverageArea.height =  ((cardCoverage.Bottom - cardCoverage.Top) * canvasScale)
		}

		this.card = new Card(this.ctx, {
			coverageArea,
			cardArea: {
				x: canvasCenter.x - this.template.width / 2,
				y: canvasCenter.y - this.template.height / 2,
				width: this.template.width,
				height: this.template.height,
			},
			image: {
				id: options.imageId,
				url: options.imageUrl
			},
			isPortrait:AppSettings.portraitMode,
			isMetalSkin: (options.skinName === "Metal"),
			uploadOnlyMode: this.uploadOnlyMode,
			cardRadius: this.isMetalSkin ? 12 : undefined //Setting a constant radius for Metal
		});

		if (options.touchDevice) {
			this.setTouchEvents();
		} else {
			// mouse events
			this.canvas.addEventListener('mousedown', e => this.startEvent(e));
			this.canvas.addEventListener('mousemove', e => this.keepEvent(e));
			this.canvas.addEventListener('mouseup', e => this.finishEvent(e));
		}

		// drag drop events
		if(this.isMetalSkin){
			this.canvas.addEventListener('dragstart', e => this.dragOver(e)); // "dragover creates issues while dragging (in metalcard), so replaced that with 'dragstart'
		} else {
			this.canvas.addEventListener('dragover', e => this.dragOver(e)); 
		}

		this.canvas.addEventListener('drop', e => this.drop(e));

		window.requestAnimationFrame(() => this.drawCard());


		/******
		For Metal skin, we have to show warning message for signature size less than certain amount, along with coverage error message
		So, we are using an object(this.testCardCoverageResult) which contains flags(true/false) for both kinds of messages separately
		******/
		if (this.isMetalSkin) {
			this.testCardCoverageResult = {
				errorCoverage: true,
				warningSize: true
			}
		}
		else {
			this.testCardCoverageResult = true;
		}

		// load template image
		const img = new Image();
		img.crossOrigin = 'Anonymous';
		img.src = AppSettings.designerSettings.DesignTemplate.UrlLarge;
		img.onload = () => {
			this.template.image = img;
			onLoaded();
		};

		// for metal card only
		this.bleedPadding = 2;

	}

	getScale() {
		return this.template.width / this.template.initialSize.width;
	}

	getPreviewScale(coverageWidth, trimmedSignWidth) {
		return trimmedSignWidth / coverageWidth;
	}

	// define touch events
	setTouchEvents() {
		const mc = new Hammer.Manager(this.canvas);
		// create a pinch and rotate recognizer
		// these require 2 pointers
		const pinch = new Hammer.Pinch();
		const rotate = new Hammer.Rotate();
		const pan = new Hammer.Pan();

		const tap = new Hammer.Tap({ event: 'singletap' });

		// we want to detect both the same time
		pinch.recognizeWith(rotate);

		// add to the Manager
		mc.add([pinch, rotate, pan, tap]);

		mc.on('rotatestart', e => {
			const obj = this.getSelectedObject();
			if(!obj) return;

			if (obj.type !== DOODLE) {
				obj.startRotate(e.rotation)
			}
		});
		mc.on('rotateend', e => {
			const obj = this.getSelectedObject();
			if(!obj) return;

			if (obj.type !== DOODLE) {
				obj.endRotate();
				this.removeItems();
			}
		});
		mc.on('rotatemove', e => {
			const obj = this.getSelectedObject();
			if(!obj) return;

			if (obj.type !== DOODLE) {
				obj.doRotate(e.rotation);
				this.testCoverage();
			}
		});
		mc.on('pinchstart', e => {

			const obj = this.getSelectedObject();
			if(!obj) return;

			if (obj.type !== DOODLE) {
				obj.startZoom();
			}
		});
		mc.on('pinchend', e => {

			const obj = this.getSelectedObject();
			if(!obj) return;

			obj.getSelectedObject().endZoom();

			//this.getSelectedObject().endZoom();
			this.removeItems();
		});
		mc.on('pinchmove', e => {
			const obj = this.getSelectedObject();
			obj.doZoom(e.scale);
			this.testCoverage();
		});
		mc.on('panstart', e => {
			const obj = this.getSelectedObject();

			if(!obj) return;

			if (obj.type !== DOODLE) {
				obj.startMove();
				this.onCanvasItemSelect(obj);
			} else {


				obj.startTouch(getTouchPos(e));
			}

		});
		mc.on('panmove', e => {
			const obj = this.getSelectedObject();

			if(!obj) return;

			if (obj.type !== DOODLE) {
				obj.doMove({ x: e.deltaX, y: e.deltaY });
				this.onCanvasItemAllOut(obj);
				this.testCoverage();
			} else {
				//obj.hover({x: e.deltaX, y: e.deltaY});
				obj.keepTouch(getTouchPos(e));
			}
		});
		mc.on('panend', e => {
		
			
			this.finishEvent(e);
		});
		mc.on('singletap', e => {

			let thisObj = this.findSelectedObject(getTouchPos(e));

			if(thisObj){
				this.onCanvasItemSelect(thisObj);
				//return this.findSelectedObject(getTouchPos(e));
				this.onCanvaDeselect(true);
				return thisObj;
			} else {
				this.onCanvaDeselect();
			}
		});
	}

	// render images on canvas
	drawCard() {

		window.requestAnimationFrame(() => this.drawCard());

		if (!this.template.image) {
			return;
		}

		//clear canvas
		this.ctx.clearRect(0, 0, this.ctx.canvas.width, this.ctx.canvas.height);

		// render card image
		this.card.render(this.ctx);

		//render other items (clipart, text, etc)
		this.items.forEach(item => item.render(this.ctx));
		
		// Render template unless it is Metal skin (whose template is handled in designer.js file only)
		if (!(this.isMetalSkin)) {
			if(AppSettings.portraitMode){
				const x = this.canvas.height / 2 - this.template.height / 2;
				const y = 0 - ((this.canvas.width - this.template.width ) / 2) - this.template.width;  //cf: tbh, I'm not sure why this has to be reversed, but it works! ?something to do with canvas coordinates after rotation.

				this.ctx.save();
				this.ctx.rotate(90 * Math.PI/180);
				this.ctx.drawImage(this.template.image, x, y, this.template.height, this.template.width);
				this.ctx.restore();
			} else {
				const x = this.canvas.width / 2 - this.template.width / 2;
				const y = this.canvas.height / 2 - this.template.height / 2;

				this.ctx.drawImage(this.template.image, x, y, this.template.width, this.template.height);
			}
		}
	}

	startEvent(e) {
		const pos = getEventPosition(e);
		let thisObj = this.findSelectedObject(pos);
		if (thisObj) {
			thisObj.click(pos);
			this.onCanvasItemSelect(thisObj);
			
			if (this.isMetalSkin) {
				this.maintainGreyOut(false);
			}
		}else{
			
			this.onCanvaDeselect(); //mousedown outside any object
		}
	}

	manageSolidImageToSubmit(solidSignatureImg, canvasWidth, canvasHeight, trimmedSignDimensions) {

		// create a temp. canvas
		const oc = document.createElement('canvas'), octx = oc.getContext('2d');
		
		// find scaling factor according to dimenssions of image uploaded
		const initial_itemArea = AppSettings.designerSettings.Coverage.Signature || AppSettings.designerSettings.Coverage.Image;
		const canvasScale = this.getScale();
		const coverageWidth = (initial_itemArea.Right - initial_itemArea.Left) * canvasScale;
		const previewScale = this.getPreviewScale(coverageWidth, trimmedSignDimensions.width);

		const solid_signImg = new Image();
		solid_signImg.src = solidSignatureImg;
		
		solid_signImg.onload = function(){ //need this for image to load first before put onto canvas otherwise canvas will be empty
			start();
		};

		const start = () => {
			// passing the canvas dimensions 'with scaling factor'
			const options_param = {
				initial_w: canvasWidth * previewScale,
				initial_h: canvasHeight * previewScale
			}
			
			// Fetch Allowed Canvas dimensions
			this.getAllowedCanvasSize(options_param)
			.then((rev_dimensions) => {
				applyRevisedDimensions(rev_dimensions);
			},
			(error) => {
				console.log(error);
			});
		}

		const applyRevisedDimensions = (rev_dimensions) => {
			// Finding a scale value based on Trimmed solid signature size & Canvas size limitation
			const allowed_canvasScale = previewScale / rev_dimensions.scale;
			
      		// Assigning the revised dimensions to canvas
			oc.width = rev_dimensions.canvasWidth;
			oc.height = rev_dimensions.canvasHeight;

			const canvasCenter = {
				x: oc.width / 2,
				y: oc.height / 2
			}

			// Scaling every other parameter being used in drawing canvas for solid Signature, with revised dimensions' scale value
			const rev_template = {
				width: this.template.width * allowed_canvasScale,
				height: this.template.height * allowed_canvasScale,
			}

			const itemArea = {
				Top: initial_itemArea.Top * canvasScale * allowed_canvasScale,
				Bottom: initial_itemArea.Bottom * canvasScale * allowed_canvasScale,
				Left: initial_itemArea.Left * canvasScale * allowed_canvasScale,
				Right: initial_itemArea.Right * canvasScale * allowed_canvasScale
			}

			// Fetching correct dimensions of SolidImage according to getting fit in coverage area completely
			const solidImg_dimensions = this.getImageDimensions(solid_signImg.width, solid_signImg.height, (initial_itemArea.Right - initial_itemArea.Left), (initial_itemArea.Bottom - initial_itemArea.Top));
			let solid_imgWidth = solidImg_dimensions[0] * canvasScale * allowed_canvasScale;
			let solid_imgHeight = solidImg_dimensions[1] * canvasScale * allowed_canvasScale;
			
			// Revising Canvas Coverage area
			const coverageArea = {
				x: canvasCenter.x - (rev_template.width / 2),
				y: canvasCenter.y - (rev_template.height / 2),
				width: rev_template.width,
				height: rev_template.height,
			};

			// Revising Signature Coverage area
			const signCoverageArea = {
				x: -rev_template.width / 2 + itemArea.Left,
				y: -rev_template.height / 2 + itemArea.Top,
				width: (itemArea.Right - itemArea.Left),
				height: (itemArea.Bottom - itemArea.Top)
			};
			
			// Draw rectangle on Revised Canvas Coverage area
			roundRect(octx, coverageArea.x, coverageArea.y, coverageArea.width, coverageArea.height, 2);

			// Translate to Revised canvasCenter to draw signature
			octx.translate(canvasCenter.x, canvasCenter.y);
			// roundRect(octx, signCoverageArea.x, signCoverageArea.y, signCoverageArea.width, signCoverageArea.height, 2);
			
			let originPoint = {
				x: signCoverageArea.x + (signCoverageArea.width / 2),
				y: signCoverageArea.y + (signCoverageArea.height / 2)
			};

			// Find out all the move, rotate, resize values of the stroke signature image
			const stroke_signItem = this.items.filter(item => item.type === "Signature")[0];
			const stroke_signEffects = {
				originPoint: {
					x: stroke_signItem.originPoint.x,
					y: stroke_signItem.originPoint.y
				},
				width: stroke_signItem.width,
				height: stroke_signItem.height,
				rotation: stroke_signItem.rotation
			};
			
			// Apply effects to solid signature image after Revising with the scaling factor
			solid_imgWidth = stroke_signEffects.width * allowed_canvasScale;
			solid_imgHeight = stroke_signEffects.height * allowed_canvasScale;
			originPoint.x = stroke_signEffects.originPoint.x * allowed_canvasScale;
			originPoint.y = stroke_signEffects.originPoint.y * allowed_canvasScale;
			const rotation = stroke_signEffects.rotation;
			
			// Translate to Revised origin points
			octx.translate(originPoint.x, originPoint.y);
			if (rotation !== 0) {
				octx.rotate(rotation * Math.PI / 180);
			}
			
			// Draw solid signature (with effects equal to stroke signature) on temp. canvas
			octx.drawImage(solid_signImg, -solid_imgWidth / 2, -solid_imgHeight / 2, solid_imgWidth, solid_imgHeight);

			// Get Absolute Coverage area of signature to run 'getImageData' function
			const signatureAbsoluteCoverageArea = {
				x: itemArea.Left,
				y: itemArea.Top,
				width: itemArea.Right - itemArea.Left,
				height: itemArea.Bottom - itemArea.Top
			};

			const imgData = octx.getImageData(signatureAbsoluteCoverageArea.x, signatureAbsoluteCoverageArea.y, signatureAbsoluteCoverageArea.width + BLEED_AREA.width, signatureAbsoluteCoverageArea.height + BLEED_AREA.height);
			
			// Create another temp. canvas to put signature only
			const tempCanvas = document.createElement('canvas');
			const tempCtx = tempCanvas.getContext('2d');
			tempCanvas.width  = signatureAbsoluteCoverageArea.width + BLEED_AREA.width;
			tempCanvas.height = signatureAbsoluteCoverageArea.height + BLEED_AREA.height;

			tempCtx.putImageData(imgData, 0, 0);
			this.croppedSignatureImage = tempCanvas.toDataURL();

			// let solidsignImg = document.createElement('IMG');
			// solidsignImg.src = croppedImage;
			// solidsignImg.id = 'final';
			// document.body.appendChild(solidsignImg);
		};
	
	}

	maintainGreyOut(flag) {
		const greyOutDiv = document.querySelector('.greyOut-wrapper');

		const itemArea = AppSettings.designerSettings.Coverage.Signature || AppSettings.designerSettings.Coverage.Image;
		
		const signatureAbsoluteCoverageArea = {
			x: (itemArea.Left / this.template.initialSize.width) * 100,
			y: (itemArea.Top / this.template.initialSize.height) * 100,
			width: ((itemArea.Right - itemArea.Left) / this.template.initialSize.width) * 100 + this.bleedPadding / 4,
			height: ((itemArea.Bottom - itemArea.Top) / this.template.initialSize.height) * 100 + this.bleedPadding / 2
		};

		if (flag) {
			greyOutDiv.classList.add('active');
			const polygon_style = this.generatePoly(
				[signatureAbsoluteCoverageArea.x, signatureAbsoluteCoverageArea.y],
				[signatureAbsoluteCoverageArea.x + signatureAbsoluteCoverageArea.width, signatureAbsoluteCoverageArea.y + signatureAbsoluteCoverageArea.height]);
			greyOutDiv.style.clipPath = polygon_style;
			greyOutDiv.style.webkitClipPath = polygon_style; //For Safari
		}
		else {
			greyOutDiv.classList.remove('active');
		}
	}

	generatePoly(p1, p2) {
		const a = p1[0] + '%';
		const b = p1[1] + '%';
		const c = p2[0] + '%';
		const d = p2[1] + '%';
		return 'polygon(' + [
			'0% 0%',
			'0% 100%',
			a + ' 100%',
			a + ' 50%',
			a + ' ' + b,
			c + ' ' + b,
			c + ' ' + d,
			a + ' ' + d,
			a + ' 100%',
			'100% 100%',
			'100% 0%'
		].join(', ') + ')';
	}

	findSelectedObject(pos) {
		// check if events happens in already selected item
		let selectedObject = null;
		let selectedItems = this.items.filter(item => item.selected === true && item.hitTest(pos));
		if (selectedItems.length === 1 && (selectedItems[0].type === this.activeMode || this.exclusiveMode !== true)) {
			selectedObject = selectedItems[0];
		} else {

			// remove previously selected object
			this.card.selected = false;
			this.items.forEach(item => item.selected = false);

			// check if  any item except doodle is selected
			for (const item of this.items.filter(item => item.type !== DOODLE )) {
					// If item is overlay
					if(item.type  === OVERLAY){
						item.selected = false;
					}	
					else
					{
						if (!selectedObject && item.hitTest(pos) && (item.type === this.activeMode || this.exclusiveMode !== true)) {
							item.selected = true;
							selectedObject = item;
						} else {
							item.selected = false;
						}
					}
			}

			// test for doodle selection
			if (!selectedObject) {
				const doodleItem = this.items.find(item => item.type === DOODLE && (item.type === this.activeMode || this.exclusiveMode !== true));
				if (doodleItem) {
					if (doodleItem.hitTest(pos)) {
						doodleItem.selected = true;
						selectedObject = doodleItem;
					} else {
						doodleItem.selected = false;
					}
				}
			}
		}

		if (selectedObject) {
			// move selected item on top;
			const sortedItems = this.items.filter(item => item !== selectedObject);
			this.items = [...sortedItems, selectedObject];

			// found an item - remove selection from card and delegate click event to selected item
			this.card.selected = false;
			return selectedObject;

		} else {

			if (this.activeMode === CARD || this.exclusiveMode !== true) {
				this.card.selected = true; // select card and pass the click event
				return this.card;
			}
		}
	}

	/*---------- setActiveMode notes mode (Doodle | Text | clipArt etc) --------------*/
	setActiveMode(mode) {
		this.activeMode = mode;

		if(mode !== DOODLE) this.deselectItems();

		if(mode === 'card'){ this.card.selected = true; } //there's only ever one background, so we can select it immediately
	}

	/*----- setExclusiveMode(true) to prevent more than one feature being active at once.---*/
	setExclusiveMode(isExclusive) {
		this.exclusiveMode = isExclusive;
	}

	// return promise with image when image is loaded
	setCardImage(id, url, imgType) {
		return this.card.setImage(id, url, imgType);
	}

	getCardImage() {
		return this.card.image;
	}



	getSelectedObject() {
		let selectedObj;
		if(this.card.selected){
			if(this.activeMode == 'card') selectedObj = this.card;
		} else {
			selectedObj = this.items.find(item => item.selected === true);
			if(selectedObj && (selectedObj.type !== this.activeMode) ) {
				return ;
			}
		}
		return selectedObj;
	}

	hasDoodle() {
		return this.items.filter(item => item.type === DOODLE).length !== 0;
	}

	keepEvent(e) {
		const pos = getEventPosition(e);

		const selectedObj = this.getSelectedObject();
		if (selectedObj) {
			selectedObj.hover(pos);
			if (selectedObj.imageMouseOver) {
				this.ctx.canvas.classList.add('imgHover');
				if(selectedObj.type !== CARD) this.onCanvasItemAllOut(selectedObj); 
			} else {
				this.ctx.canvas.classList.remove('imgHover');
			}
			this.testCoverage(selectedObj);
		}
	}

	//result set to true if card coverage is OK
	testCoverage() {

		/* For Metal, we need to check both errorCoverage & warningSize property in signature object */
		if (this.isMetalSkin) {
			let result = {};
			result.errorCoverage = this.items.filter(i => i.errorCoverage === true).length === 0;
			result.warningSize = this.items.filter(i => i.warningSize === true).length === 0;

			// if result is different from previous check perform callback
			if(JSON.stringify(result) != JSON.stringify(this.testCardCoverageResult)) {
				this.handleCardCoverage(result);
				this.testCardCoverageResult = result;
			}
		}
		else {
			// check errorCoverage property in all objects
			let result = !this.card.errorCoverage && this.items.filter(i => i.errorCoverage === true).length === 0;

			// if result is different from previous check perform callback
			if (result !== this.testCardCoverageResult) {
				this.handleCardCoverage(result);
				this.testCardCoverageResult = result;
			}
		}
	}

	finishEvent(e) {

		const selectedObj = this.getSelectedObject();

		// only kbc have the undo feature
		if(AppSettings.designerName === DESIGNER.KBC) {
			this.manageEffectsObj(selectedObj);
		}
		
		if (selectedObj) {
			if (selectedObj.type !== DOODLE) {
				if(selectedObj.type !== CARD && selectedObj.removed){
					this.onCanvaDeselect();
				}
			
				selectedObj.endMove(e,selectedObj.id);
				//this.removeItems(); //not sure if we need this or not
			} 

			if(selectedObj.type === CARD) { var cardScale = selectedObj.getScale(); } 

			if(selectedObj.type === CARD || selectedObj.removed) {
				this.onCanvaDeselect();  //re-enables manipulation controls
			} else if (selectedObj.type === TEXT || selectedObj.type === IMAGE || selectedObj.type === LOGO){
				this.onCanvaDeselect(selectedObj.selected); //re-enables controls without resetting them
			} 
			
			selectedObj.done();
			
			if (this.isMetalSkin) {
				this.maintainGreyOut(true);
			}
		}
		
		this.removeItems(); 
	}

	// For Undo functionality
	manageEffectsObj(selectedObj) {
		const effectObj = {
			flipped: selectedObj.flipped,
			flippedV: selectedObj.flippedV,
			originPoint: {
				x: selectedObj.originPoint.x,
				y: selectedObj.originPoint.y
			},
			width: selectedObj.width,
			height: selectedObj.height,
			rotation: selectedObj.rotation,
			resizeValue: document.querySelector('.aam-designer-init .designer-page .controls-container .input-container #scale-control').value
		}
		AppSettings.undoEffectsArr.push(effectObj);
	}
	
	removeItems() {
		// remove items that out of template area by omitting them from redraw list
		const items = this.items.filter(i => i.removed !== true);
		if (items.length !== this.items.length) {
			// make card selected
			this.card.selected = true;
			this.items = items;
		}
	}

	removeCurrentLogo() {
		const currentLogo = this.items.filter(i => i.type === LOGO)[0];
		if(currentLogo) currentLogo.removed = true;

		this.removeItems();
	}

	removeCurrentMonogram() {
		const currentLogo = this.items.filter(i => i.type === MONOGRAM)[0];
		if(currentLogo) currentLogo.removed = true;

		this.removeItems();
	}

	clearSignature() {
		const obj = this.getSelectedObject();
		const items = this.items.filter(i => i.removed !== true);
		if (items.length > 0) {
			items[0].removed = true;

			// needs to write better condition (error found by Neeru)
			if (obj != undefined && obj.type == SIGNATURE) {
				this.card.selected = true;
				this.items = items;
			}
		}
	};

	clearMonogram() {
	
		const obj = this.getSelectedObject();
		const items = this.items.filter(i => i.removed !== true);
		if (items.length > 0) {
			items[0].removed = true;

			// needs to write better condition (error found by Neeru)
			if (obj != undefined && obj.type == MONOGRAM) {
				this.card.selected = false;
				this.items = items;
			}
		}
	};

	clearLogo() {

		const obj = this.getSelectedObject();
		const items = this.items.filter(i => i.removed !== true);
		items[0].removed = true;

		if (obj.type == LOGO) {
			obj.endMove();
		}
	}

	setImage(imageId, imageUrl) {
		this.card.setImage(imageId, imageUrl);
	}

	getSignatureCoverageArea() {
		//use default coverage if feature coverage not available
		const itemArea = AppSettings.designerSettings.Coverage.Signature || AppSettings.designerSettings.Coverage.Image;
		const canvasScale = this.getScale();

		const templateX = (this.canvas.width - this.template.width) / 2;
		const templateY = (this.canvas.height - this.template.height) / 2;

		const signatureAbsoluteCoverageArea = {
			x: (itemArea.Left * canvasScale) + templateX,
			y: (itemArea.Top * canvasScale) + templateY,
			width: ((itemArea.Right - itemArea.Left) * canvasScale),
			height: ((itemArea.Bottom - itemArea.Top) * canvasScale)
		};

		return signatureAbsoluteCoverageArea;
	}

	//reset card image
	reset() {
		this.card.reset();
		this.testCoverage();
	}

	resetImage(resetObj) {
		this.getSelectedObject().reset(resetObj);
	}

	// Undo card image
	undo() {
		this.card.undo(true);
		this.testCoverage();
	}

	// undo specific effect panel
	undoSpecificEffect() {
		this.card.undo();
		this.testCoverage();
	}

	dragOver(e) {
		e.preventDefault();
	}

	drop(e) {
		e.preventDefault();

		const data = JSON.parse(e.dataTransfer.getData('text'));

		const imageId = +data.id;
		const imageUrl = data.url;
		if (imageId) {
			const pos = getEventPosition(e);
			const cardItem = this.addImageItem(imageId, imageUrl, { initialPosition: pos });
			cardItem.originPoint = getEventPosition(e);
		}
	}

	selectItems() {
		this.items.forEach(item => {
			item.selected = true;
		});
	}

	deselectItems() {
		this.items.forEach(item => {
			item.selected = false;
		});
	}

	setStrokeImageForPreview(strokeImg) {
		this.stokeSignImg = strokeImg;
	}

	// This is for adding Logo/Clipart as both are images
	addImageItem(id, url, options = {}) {

		const activeMode = this.activeMode;

		//use default coverage if feature coverage not available
		//const itemArea = AppSettings.designerSettings.Coverage.Logo || AppSettings.designerSettings.Coverage.Image; 
		const itemArea = (options.layerType) ? AppSettings.designerSettings.Coverage[options.layerType] : AppSettings.designerSettings.Coverage.Image; 

		// remove previously selected object
		this.card.selected = false;
		this.items.forEach(item => item.selected = false);

		const canvasScale = this.getScale();

		// area where image is still allowed (can be template area), after moving out of the area item will be removed
		const allowedArea = {
			width: this.template.width,
			height: this.template.height,
			x: -this.template.width / 2,
			y: -this.template.height / 2
		};

		let coverageArea = {
			width: ((itemArea.Right - itemArea.Left) * canvasScale),
			height: ((itemArea.Bottom - itemArea.Top) * canvasScale)
		}
		
		if(AppSettings.portraitMode) {
			coverageArea.x =  (this.template.width / 2) - (itemArea.Bottom * canvasScale);
			coverageArea.y =  (-this.template.height / 2 + itemArea.Left * canvasScale);
		} else {
			coverageArea.x =   (-this.template.width / 2 + itemArea.Left * canvasScale);
			coverageArea.y =   (-this.template.height / 2 + itemArea.Top * canvasScale);
		}

		const cardItem = new ImageItem(this.ctx, {
			id,
			url,
			activeMode
		}, {
				canvasScale,
				coverageArea,
				allowedArea,
				initialPosition: options.initialPosition,
				width: 50 * canvasScale,
				height: 50 * canvasScale,
				type: options.type,
				layerType: options.type === LOGO ? CUSTOM_IMAGE : STOCK_IMAGE,
				isPortrait: AppSettings.portraitMode
			});

		if (options.type) {
			cardItem.type = options.type;
			if (options.type === LOGO) {
				cardItem.layerType = CUSTOM_IMAGE;
			}
		}

		this.items.push(cardItem);

		//this.removeItems();

		return cardItem;

	}

	addOverlayItem(id, url, options = {}){

		const activeMode = options.activeMode;
		const itemArea = AppSettings.designerSettings.Coverage.Image; 

		// remove previously selected object
		this.card.selected = false;

		this.items.forEach(item => item.selected = false);

		const canvasScale = this.getScale();

		// area where image is still allowed (can be template area), after moving out of the area item will be removed

		const allowedArea = {
			width: this.template.width,
			height: this.template.height,
			x: -this.template.width / 2,
			y: -this.template.height / 2
		};

		const coverageArea = {
			x: (-this.template.width / 2 + itemArea.Left * canvasScale),
			y: (-this.template.height / 2 + itemArea.Top * canvasScale),
			width: ((itemArea.Right - itemArea.Left) * canvasScale),
			height: ((itemArea.Bottom - itemArea.Top) * canvasScale)
		};

		const intialPosition = {
			x : 0,
			y : 0
		}

		const cardItem = new OverlayImageItem(this.ctx, {
			id,
			url,
			activeMode
		}, {
				coverageArea,
				allowedArea,
				selected : false,
				width: this.template.width,
				initialPosition: intialPosition,
				height: this.template.height,
				type: options.layerType,
				//layerType: options.layerType === OVERLAY ? CUSTOM_IMAGE : STOCK_IMAGE,
				layerType: STOCK_IMAGE,
				isPortrait: AppSettings.portraitMode
			});	

			if(this.items[0] && this.items[0].type === LAYER_TYPE.OVERLAY){
				this.items[0] = cardItem
			} else {
				this.items.unshift(cardItem);
			} 

			return cardItem;
	}

	
	removeCurrentOverlay(){
		const currentOverlay = this.items.filter(i => i.type === OVERLAY)[0];
		if(currentOverlay) currentOverlay.removed = true;
		this.removeItems();	
    }


/*	removeCurrentMonogram(){
		const currentOverlay = this.items.filter(i => i.type === MONOGRAM)[0];
		if(currentOverlay) currentOverlay.removed = true;
		this.removeItems();	
	}*/

	// This is for adding signature
	addSignatureItem(id, url, imgWidth, imgHeight, options = {}, cb) {

		const activeMode = this.activeMode;
		const isMetalSkin = this.isMetalSkin;

		//use default coverage if feature coverage not available
		const itemArea = AppSettings.designerSettings.Coverage.Signature || AppSettings.designerSettings.Coverage.Image;

		// remove previously selected object
		this.card.selected = false;
		this.items.forEach(item => item.selected = false);

		const canvasScale = this.getScale();

		// area where image is still allowed (can be template area), after moving out of the area item will be removed
		// Making it empty as we don't want the signature to be deleted at all
		const allowedArea = {};

		const templateX = (this.canvas.width - this.template.width) / 2;
		const templateY = (this.canvas.height - this.template.height) / 2;

		const signatureAbsoluteCoverageArea = {
			x: (itemArea.Left * canvasScale) + templateX,
			y: (itemArea.Top * canvasScale) + templateY,
			width: ((itemArea.Right - itemArea.Left) * canvasScale),
			height: ((itemArea.Bottom - itemArea.Top) * canvasScale)
		};

		const coverageArea = {
			x: (-this.template.width / 2 + itemArea.Left * canvasScale),
			y: (-this.template.height / 2 + itemArea.Top * canvasScale),
			width: ((itemArea.Right - itemArea.Left) * canvasScale) + this.bleedPadding,
			height: ((itemArea.Bottom - itemArea.Top) * canvasScale) + this.bleedPadding
		};

		let dimensions = this.getImageDimensions(imgWidth, imgHeight, (itemArea.Right - itemArea.Left), (itemArea.Bottom - itemArea.Top));
		imgWidth = dimensions[0];
		imgHeight = dimensions[1];

		const cardItem = new ImageItem(this.ctx, {
			id,
			url,
			activeMode
		}, {
				isMetalSkin,
				itemArea,
				canvasScale,
				coverageArea,
				signatureAbsoluteCoverageArea,
				allowedArea,
				initialPosition: options.initialPosition,
				width: imgWidth * canvasScale,
				height: imgHeight * canvasScale,
				type: options.type,
				layerType: CUSTOM_IMAGE
				// layerType: options.type === SIGNATURE ? CUSTOM_IMAGE : STOCK_IMAGE
			});

		// Need to check this condition's use
		// if (options.type) {
		// 	cardItem.type = options.type;
		// 	if (options.type === SIGNATURE) {
		// 		cardItem.layerType = CUSTOM_IMAGE;
		// 	}
		// }
		
		this.items.push(cardItem);
		
		// Calling the setImage function again to get the response after img is loaded in Promise
		const img_data = cardItem.setImage(url, CUSTOM_IMAGE);
		img_data.then((data) => {
			if(cb != undefined) {
				cb();
			}
		});

		return cardItem;
	}

	getImageDimensions(imgWidth, imgHeight, areaWidth, areaHeight) {
		if (imgWidth <= areaWidth && imgHeight <= areaHeight) {
			return [imgWidth, imgHeight];
		}
		else {
			const widthRatio = areaWidth / imgWidth; //in order to fit the item width wise in coverage area
			const width = Math.round(imgWidth * widthRatio);
			const height = Math.round(imgHeight * widthRatio);
			if (width <= areaWidth && height <= areaHeight) {
				return [width, height];
			}
			else {
				const heightRatio = areaHeight / imgHeight; //in order to fit the item height wise in coverage area
				return [Math.round(imgWidth * heightRatio), Math.round(imgHeight * heightRatio)];
			}
		}
	}

	addMonogramItem(text,options={}){	
		
		this.removeCurrentMonogram()
		let itemArea;

		// set default coverage if not available from PCS
		if (AppSettings.designerSettings.Coverage.ClipArt) {
			itemArea = AppSettings.designerSettings.Coverage.ClipArt;
		} else {
			itemArea = this.defaultCoverage;
		}

		// remove previously selected object
		this.card.selected = false;

		this.items.forEach(item => item.selected = false);

		const canvasScale = this.getScale();

		const canvasCenter = {
			x: this.canvas.width / 2,
			y: this.canvas.height / 2
		};

		const allowedArea = {
			width: this.template.width,
			height: this.template.height,
			x: -this.template.width / 2,
			y: -this.template.height / 2
		};

		const coverageArea = {
			x: (-this.template.width / 2 + itemArea.Left * canvasScale),
			y: (-this.template.height / 2 + itemArea.Top * canvasScale),
			width: ((itemArea.Right - itemArea.Left) * canvasScale),
			height: ((itemArea.Bottom - itemArea.Top) * canvasScale)
		};

		options.fontStyle.height = 50
	
		const mgItem = new MonogramItem(text, this.ctx, {
			canvasScale,
			coverageArea,
			allowedArea,
			fontStyle:options.fontStyle,
			coordinates : options.coordinates,
			layerType: CUSTOM_IMAGE
		});

		this.items.push(mgItem);
		return mgItem;

	}

	addTextItem(id, text, options = {}) {

		let itemArea;

		//set default coverage if not available from PCS
		if (AppSettings.designerSettings.Coverage.Logo) {
			itemArea = AppSettings.designerSettings.Coverage.Logo;
		} else {
			itemArea = this.defaultCoverage;
		}

		// remove previously selected object
		this.card.selected = false;
		this.items.forEach(item => item.selected = false);

		const canvasScale = this.getScale();

		const canvasCenter = {
			x: this.canvas.width / 2,
			y: this.canvas.height / 2
		};

		const allowedArea = {
			width: this.template.width,
			height: this.template.height,
			x: -this.template.width / 2,
			y: -this.template.height / 2
		};

		/*const coverageArea = {
			x: (-this.template.width / 2 + itemArea.Left * canvasScale),
			y: (-this.template.height / 2 + itemArea.Top * canvasScale),
			width: ((itemArea.Right - itemArea.Left) * canvasScale),
			height: ((itemArea.Bottom - itemArea.Top) * canvasScale)
		};*/

		//--------------------------------------------------
		let coverageArea = {
			width: ((itemArea.Right - itemArea.Left) * canvasScale),
			height: ((itemArea.Bottom - itemArea.Top) * canvasScale)
		}
		
		if(AppSettings.portraitMode) {
			coverageArea.x =  (this.template.width / 2) - (itemArea.Bottom * canvasScale);
			coverageArea.y =  (-this.template.height / 2 + itemArea.Left * canvasScale);
		} else {
			coverageArea.x =   (-this.template.width / 2 + itemArea.Left * canvasScale);
			coverageArea.y =   (-this.template.height / 2 + itemArea.Top * canvasScale);
		}
		//--------------------------------------------------

		options.fontStyle.height = Math.floor( options.fontStyle.height * canvasScale );

		const textItem = new TextItem(id, text, this.ctx, {
			canvasScale,
			coverageArea,
			allowedArea,
			initialPosition: options.initialPosition,
			fontStyle:options.fontStyle,
			isPortrait: AppSettings.portraitMode
		});
		this.items.push(textItem);

		return textItem;
	}


	addDoodleItem(options = {}) {

		//const itemArea = AppSettings.designerSettings.Coverage.Logo;
		let itemArea;

		//set default coverage if not available from PCS
		if(AppSettings.designerSettings.Coverage.Doodle){
			itemArea = AppSettings.designerSettings.Coverage.Doodle;
		}else{
			itemArea = this.defaultCoverage;
		}

		// remove previously selected object
		this.card.selected = false;
		this.items.forEach(item => item.selected = false);

		const canvasScale = this.getScale();
		const bleedArea = 12;

		//coverage is relative to lscape template in PCS UI, so we need to rotate it for portrait mode
		const itemLeft = (AppSettings.portraitMode)? itemArea.Top : itemArea.Left;
		const itemTop =  (AppSettings.portraitMode)? itemArea.Left : itemArea.Top;
		const itemRight =  (AppSettings.portraitMode)? itemArea.Bottom : itemArea.Right;
		const itemBottom =  (AppSettings.portraitMode)? itemArea.Right : itemArea.Bottom;

		/*const coverageArea = {
			x: -this.template.width / 2 + itemLeft * canvasScale,
			y: -this.template.height / 2 + itemTop * canvasScale,
			width: (itemRight - itemLeft) * canvasScale - bleedArea,   
			height: (itemBottom - itemTop) * canvasScale - bleedArea    
		};*/

		let coverageArea = {
			width: ((itemArea.Right - itemArea.Left) * canvasScale) - bleedArea ,
			height: ((itemArea.Bottom - itemArea.Top) * canvasScale) - bleedArea
		}
		
		if(AppSettings.portraitMode) {
			coverageArea.x =  (this.template.width / 2) - (itemArea.Bottom * canvasScale);
			coverageArea.y =  (-this.template.height / 2 + itemArea.Left * canvasScale);
		} else {
			coverageArea.x =   (-this.template.width / 2 + itemArea.Left * canvasScale);
			coverageArea.y =   (-this.template.height / 2 + itemArea.Top * canvasScale);
		}
		
		const doodleItem = new DoodleItem({
			coverageArea,
			lineWidth: options.lineWidth,
			lineColor: options.lineColor,
			isPortrait: AppSettings.portraitMode
		});

		this.items.push(doodleItem);

		return doodleItem;
	}

	disableDoodle() {
		this.card.selected = true;
		//this.items.forEach(item => item.selected = true);
		
		// removing newly doodle item
		this.items.pop();
	}

	moveUp(val) {
		this.getSelectedObject().moveUp(val);
		this.testCoverage();
	}

	moveDown(val) {
		this.getSelectedObject().moveDown(val);
		this.testCoverage();
	}

	moveLeft(val) {
		this.getSelectedObject().moveLeft(val);
		this.testCoverage();
	}

	moveRight(val) {
		this.getSelectedObject().moveRight(val);
		this.testCoverage();
	}

	rotate(val) {
		this.getSelectedObject().rotate(val);
		this.testCoverage();
	}


	rotateSingle(val) {
		this.getSelectedObject().rotateSingle(val);
		this.testCoverage();
	}

	flip() {
		this.getSelectedObject().flip();
		this.testCoverage();
	}

	// Flip vertical
	flipV() {
		this.getSelectedObject().flipV();
		this.testCoverage();
	}

	scale(val) {
		this.getSelectedObject().scale(val);
		this.testCoverage();
	}

	// Working fine with axis designer
	resizeObject(val) {
		this.getSelectedObject().scale(val);
		this.testCoverage();
	}

	handleResize(w,h) {

		//get current dimensions of #ssg-canvas element
		const width =  (w && h)?  w : Math.floor(this.canvas.getBoundingClientRect().width);			
		const height = (w && h)?  h : Math.floor(this.canvas.getBoundingClientRect().height);

		this.canvasScale =  width / this.canvas.width;  //width property, not physical width

		this.canvas.width = width;
		this.canvas.height = height;
		this.card.handleResize(this.canvasScale);
		this.items.forEach(item => item.handleResize(this.canvasScale));

		this.template.width *= this.canvasScale;
		this.template.height *= this.canvasScale;
	}

	// create snapshot of the canvas as as png image
	getPreviewImage(width = 400, height = 400) {

		const previewCanvas = document.createElement('canvas');
		previewCanvas.width = width;
		previewCanvas.height = height;
		const previewCanvasContext = previewCanvas.getContext('2d');

		const scale = {
			width: width / this.template.width,
			height: height / this.template.height
		};
		 this.card.preview(scale, previewCanvasContext);

		this.items.forEach(item => {			
			item.preview(scale, previewCanvasContext);
		});

		// Add template unless it is Metal skin
		if (!(this.isMetalSkin)) {
			if(!AppSettings.portraitMode) previewCanvasContext.drawImage(this.template.image, 0, 0, this.template.width * scale.width, this.template.height * scale.height);
		}
		return previewCanvas.toDataURL();
	}

	async getPreviewImageMetal(width = 400, height = 400) {

		const previewCanvas = document.createElement('canvas');
		previewCanvas.width = width;
		previewCanvas.height = height;
		const previewCanvasContext = previewCanvas.getContext('2d');

		const scale = {
			width: width / this.template.width,
			height: height / this.template.height
		};
		 this.card.preview(scale, previewCanvasContext);

		await this.previewStrokeSign(scale,previewCanvasContext);

		return previewCanvas.toDataURL();
	}

	async previewStrokeSign(scale, previewCanvasContext)
	{
		return new Promise(resolve => {
			this.items.forEach(async item => {
				await item.setStrokeImage(this.stokeSignImg);
					// it was successful
				item.previewMetal(scale, previewCanvasContext);
				resolve(previewCanvasContext);
			})			
		})
	}

	// update image object with new processed image
	updateImageObject(uploadRequestResult) {
		this.items.forEach((item, i) => {
			if (item.type === 'Signature') {
				// console.log(item);
				item.id = uploadRequestResult.Id; //updating image id with the id of processed image
				item.rotation = 0; //because we are sending the whole coverage area's snapshot, so no need to send rotation separately
			}
		});
		this.submit();
	}

	// submit canvas to the PCS
	submit() {

		let layers;

		// No need to submit template/card layer in case of metal skin
		if (this.isMetalSkin) {
			layers = this.items;
		}
		else {
			layers = [this.card, ...this.items];
		}

		let count = 0;
		const cardLayers = [];

		const canvasScale = this.getScale();

		const canvasCenter = {
			x: this.canvas.width / 2,
			y: this.canvas.height / 2
		};

		const templateLetTopCorner = {
			x: (canvasCenter.x - this.template.width / 2) / canvasScale,
			y: (canvasCenter.y - this.template.height / 2) / canvasScale
		};

		//upload custom images as texts doodles etc if required
		this.uploadCustomImages().then(() => {

			//return;
			
			layers.forEach((layer, i) => {

				// submit layer
				api.submitLayer(AppSettings.handoverKey, layer.layerType).then(layerConfig => {
					// console.log('layerConfig', layerConfig);

					count++;

					// get scaled layer coordinates
					const layerCoords = layer.getBoundRect();
					const layerWidth = (layerCoords.right - layerCoords.left) / canvasScale;
					const layerHeight = (layerCoords.bottom - layerCoords.top) / canvasScale;
					const layerLeftTopCorner = {
						x: (layerCoords.left + canvasCenter.x) / canvasScale - templateLetTopCorner.x,
						y: (layerCoords.top + canvasCenter.y) / canvasScale - templateLetTopCorner.y
					};

					const templateRotatedH = this.template.width/canvasScale;
					const templateRotatedW = this.template.height/canvasScale;

					// text layer submitted as already rotated and flipped
					const rotation = layer.type === TEXT ? 0 : Math.floor(layer.rotation);
					const flip = layer.type === TEXT ? 0 : layer.flipped ? 1 : 0;
					const flipV = layer.type === TEXT ? 0 : layer.flippedV ? 1 : 0;

					let config;

					if(this.isMetalSkin){
						const itemArea = AppSettings.designerSettings.Coverage.Signature || AppSettings.designerSettings.Coverage.Image;
						config = {
							Left: Math.floor(itemArea.Left),
							Top: Math.floor(itemArea.Top),
							Right: Math.floor(itemArea.Right),
							Bottom: Math.floor(itemArea.Bottom),
							Rotation: rotation
						};
					}
 					//In portrait mode, currently only the card layer is rotated.
					else if (AppSettings.portraitMode) {
						if(layer.type === CARD  || layer.type === IMAGE || layer.type === LOGO || layer.type === DOODLE || layer.type === MONOGRAM ){
							config = {
							  	Bottom: (0 - Math.floor(layerLeftTopCorner.x)) + templateRotatedH,
							    Top: 	Math.floor(((0 - layerLeftTopCorner.x) + templateRotatedH ) - layerWidth),
								Left:   Math.floor(layerLeftTopCorner.y),
								Right:  Math.floor(layerLeftTopCorner.y + layerHeight),
								Rotation: rotation - 90
							} 
						} else {
							//on standby for further feature elements ----------
							config = {
								Bottom: Math.floor(layerLeftTopCorner.x + layerWidth),
								Top: Math.floor(layerLeftTopCorner.x),
								Left: Math.floor(layerLeftTopCorner.y),
								Right: Math.floor(layerLeftTopCorner.y + layerHeight),
								Rotation: rotation 
							}
						}
					}
					else {
						config = {
							Bottom: Math.floor(layerLeftTopCorner.y + layerHeight),
							Top: Math.floor(layerLeftTopCorner.y),
							Left:   Math.floor(layerLeftTopCorner.x),
							Right: Math.floor(layerLeftTopCorner.x + layerWidth),
							Rotation: rotation
						}
					}

					config.Flip = flip;
					config.FlipV = flipV

					//NB: config properties need to be integer values.

					layerConfig.Configuration = config;
					layerConfig.ImageId = layer.id;
					layerConfig.Order = i;
					if (layer.type === CARD) {
						// set special effects if any
						if (layer.specialEffectImage) {
							layerConfig.SpecialEffectFilter = {
								Name: layer.specialEffectName,
								Intensity: layer.specialEffectIntensity
							};
						}
					}

					cardLayers.push(layerConfig);

					//weird way to wait for all card layers submission
					if (count === layers.length) {
						// console.log('perform  submit request', layerConfig);
						submitCard(cardLayers);
					}
				});

			});

		}).catch(error => {
			console.error('Can\'t upload custom image', error);
		});

		function submitCard(layers = []) {

			const data = AppSettings.client;
			data.Layers = layers;
			// if( AppSettings.DataCapture) {
			// 	data.CustomDataCapture = AppSettings.DataCapture;
			// }

			api.submitCard(AppSettings.handoverKey, AppSettings.clientId, data).then(res => {
				// console.log('submit response: ' + AppSettings.designerSettings.Handback.Url, res);

				// If response is not null
				if (res) {

					const skin = AppSettings.designerSettings.Skin.SwfFilename;
					
					if (skin === 'CVS_Metal') {

						const url = AppSettings.handback.url || AppSettings.designerSettings.Handback.Url;
						const handbackMethod = AppSettings.designerSettings.Handback.Method;

						const formData = {
							imgType: (AppSettings.isCustomImage) ? 'Custom' : 'Gallery',
							Handover_Key: AppSettings.handoverKey,
							LanguageId: data.LanguageId,
							emailaddr: AppSettings.emailAddress,
							cardimageid: data.CardImageId
						};

						// Passthrough handling
						const passthrough = AppSettings.passThrough;
						let passThroughFields = '';
						if (passthrough) {
							let passThroughArr = [];
							for (let [key, value] of Object.entries(passthrough)) {
								passThroughArr.push('&' + key + '=' + value);
								formData[key] = value;
							}
							
							// all fields together with one string
							let passThroughString = "";
							for (let i = 0; i < passThroughArr.length; i++) {
								passThroughString += passThroughArr[i];
							}
							passThroughFields = passThroughString;
						}

						const omitEmail = !(AppSettings.designerSettings.Handback && AppSettings.designerSettings.Handback.HandbackEmailAddress);

						if (omitEmail) delete formData['emailaddr'];

						if (handbackMethod === 'post') {
							api.postRedirect(url, formData);
						} else {
							let handbackUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + "Handover-Key=" + AppSettings.handoverKey
								+ "&CardImageId=" + formData.cardimageid
								+ "&ImageType=" + formData.imgType
								+ "&LanguageId=" + formData.LanguageId;						

							if (!omitEmail) handbackUrl += "&emailaddress=" + formData.emailaddr;

							handbackUrl += passThroughFields;

							api.getRedirect(handbackUrl);
						}

						if (AppSettings.instanceSettings.callbacks && AppSettings.instanceSettings.callbacks.onSubmit) {
							AppSettings.instanceSettings.callbacks.onSubmit(res);
						}

					} else {

						//this is post response from the submit operation, so we redirect the window at ths point.
						//use the location derived from AppSettings.designerSettings.Handback.Url
						//and add params:- Handover_Key, CardImageId, emailaddress, ImageType, LanguageId

						const selectedCost = AppSettings.selectedPrice;

						function costCodeFeature(methodType) {
							let costCodeField = [];
							let costCode;

							// check if cost code exist
							if(selectedCost) {
								// Submit COST Code only for selected Price
								const indexOfCode = AppSettings.costList.indexOf(selectedCost);
								const isCostExist = AppSettings.costList.includes(selectedCost);
								let decodedPrice;
								if(isCostExist) {
									decodedPrice = AppSettings.costCodeList[indexOfCode];
								} else {
									decodedPrice = 'NULL'
								}
								
								costCode = decodedPrice;

								// check method type get or post and change data respectly
								if(methodType === 'post') {
									costCodeField.push('post');
									costCodeField.push(costCode);
								} else if(methodType === 'get') {
									costCodeField.push('get')
									costCodeField.push(costCode)
								}
								
							} else {
								costCodeField = [];
							}

							return costCodeField;
						}
						
						
						// if options have handback url it will be higher priority - Pick URL only
						let url = (AppSettings.handback && AppSettings.handback.url)?  App.AppSettings.url : AppSettings.designerSettings.Handback.Url;
						let handbackMethod = AppSettings.designerSettings.Handback.Method;

						let formData = {
							imgType: (AppSettings.isCustomImage) ? 'Custom' : 'Gallery',
							Handover_Key: AppSettings.handoverKey,
							LanguageId: data.LanguageId,
							emailaddr: AppSettings.emailAddress,
							cardimageid: data.CardImageId
						};


						const isCostCodeGet = costCodeFeature('get')[0] ==='get';
						const isCostCodePost = costCodeFeature('post')[0] ==='post';
						if(isCostCodePost) {
							formData.costCode = costCodeFeature('post')[1];
						}


						// Passthrough handling
						const passthrough = AppSettings.passThrough;
						let passThroughFields = '';
						if (passthrough) {
							let passThroughArr = [];
							for (let [key, value] of Object.entries(passthrough)) {
								passThroughArr.push('&' + key + '=' + value);
								formData[key] = value;
							}

							// all fields together with one string
							let passThroughString = "";
							for (let i = 0; i < passThroughArr.length; i++) {
								passThroughString += passThroughArr[i];
							}
							passThroughFields = passThroughString;
						}

						let omitEmail = !(AppSettings.designerSettings.Handback && AppSettings.designerSettings.Handback.HandbackEmailAddress);

						if (omitEmail) delete formData['emailaddr'];

						if (handbackMethod === 'post') {
							api.postRedirect(url, formData);
						} else {
							let handbackUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + "Handover-Key=" + AppSettings.handoverKey
								+ "&CardImageId=" + formData.cardimageid
								+ "&ImageType=" + formData.imgType
								+ "&LanguageId=" + formData.LanguageId
								+ "&costCode=" + formData.costCode;
								if(isCostCodeGet) handbackUrl += "&costCode=" + costCodeFeature('get')[1];

							if (!omitEmail) handbackUrl += "&emailaddress=" + formData.emailaddr;

							handbackUrl += passThroughFields;

							api.getRedirect(handbackUrl);
						}

						if (AppSettings.instanceSettings.callbacks && AppSettings.instanceSettings.callbacks.onSubmit) {
							AppSettings.instanceSettings.callbacks.onSubmit(res);
						}
					}

				} else {
					alert('Oops! something weird  has just happened');
					// Reloading browser
					document.location.reload(true)
					}
				}
			);
		}

	}

	uploadCustomImages() {

		const width = AppSettings.designerSettings.Printer.Size.Width;
		const height = AppSettings.designerSettings.Printer.Size.Height;

		const scale = {
			width: width / this.template.width,
			height: height / this.template.height
		};

		const uploadCustomImage = async (item, size, scale) => {

			//using 'doodle' customImgType pending fixes in PCS
			const customImgType = 9; //CUSTOM_IMAGE_TYPE_ID[item.type]? CUSTOM_IMAGE_TYPE_ID[item.type] : CUSTOM_IMAGE_TYPE_ID.doodle;//hmmm... seems v3 api not ready for this yet.

			//const imageData = await item.getCustomImage({ width, height }, scale).then((result) =>{ this.debugShowImage(result); });
			const imageData = await item.getCustomImage({ width, height }, scale);

			const res = await api.uploadCustomImage(AppSettings.handoverKey, AppSettings.clientId, 1, customImgType, imageData); 

			return new Promise(resolve => {

				item.id = res ? res.Id : null;
				resolve(item.id);

			});

		};

		const requests = this.items.filter(item => item.type === TEXT || item.type === DOODLE || item.type === MONOGRAM).map(item => uploadCustomImage(item, {
			width,
			height
		}, scale));

		return Promise.all(requests);
	}

	//can use this to show the image being uploaded (for debug purposes) - but it will break the submission, so don't call it for release version.
	debugShowImage(blob) {

		var uri = URL.createObjectURL(blob);
		var img = new Image();
		img.src = uri;
		img.id = "debugImg";

		document.body.appendChild(img);
	}

	//  text functionality
	getFontStyle() {
		const obj = this.getSelectedObject();
		if (obj.type === TEXT) {
			return obj.getFontStyle();
		}
	}

	setFontStyle(fontStyle) {
		const obj = this.getSelectedObject();
		if (obj.type === TEXT) {
			obj.setFontStyle(fontStyle);
		}
	}

	setText(text) {
		const obj = this.getSelectedObject();
		if (obj.type === TEXT) {
			obj.setText(text);
		}
	}

	getAllowedCanvasSize(options) {
		const initial_w = options.initial_w,
			initial_h = options.initial_h;

		return new Promise((resolve, reject) => {
			// Modifying image dimensions based on Canvas MaxSize Limitation
			const canvas_maxSize = 16777216;
			const canvas_size = initial_w * initial_h;
			const rev_dimensions = {
				canvasWidth: '',
				canvasHeight: '',
			  	scale: ''
			};
			let divide_factor = 1;
	
			if(Math.round(canvas_size) > canvas_maxSize) {
			  // Reducing the dimensions based on divide_factor
			  divide_factor = canvas_size / canvas_maxSize;
			  rev_dimensions.canvasWidth = initial_w / divide_factor;
			  rev_dimensions.canvasHeight = initial_h / divide_factor;
			}
			else {
			  // Assigning same width & height as of image
			  rev_dimensions.canvasWidth = initial_w;
			  rev_dimensions.canvasHeight = initial_h;
			}

			rev_dimensions.scale = divide_factor;

			resolve(rev_dimensions);
		});
	}

}

export default Canvas;